// ChatPanel — 中央下のテキストチャット枠。
//
// Phase 3 前半: シンプルな log + 1行入力。
// Phase 7 Task 7-9: スラッシュコマンド (4 種) を入力 → application 層の
// `parseSlashCommand` / `dispatchSlashCommand` 経由で処理. `/imagepaste` は
// hidden な `<input type="file">` を programmatic に open し、選択された
// File を `onImagepasteFile` (= `useImagepaste.start`) に渡す経路.
// 入力 UI 経路:
//   text → trim → '/' で始まれば slash command parser → side-effects
//                それ以外なら従来の `onSend(trimmed)` (chat.send 送信).
//
// Phase 7 Task 7-10b: chat フィルタ (旧設計、 render 段で `filterChatByUids` 適用).
//
// ユーザ確認 2026-05-06 で **future-only** に仕様統一 (drawing engine と同方針).
//   - SessionProvider が `chat.message` 受信時点で `shouldDispatchChatMessage`
//     で gate, フィルタ ON 中の uid からの message は reducer に流さず捨てる.
//   - 結果、`view.chat` には filter 対象 message が積まれない → 解除しても
//     復活しない (= drawing engine の `setStrokeFilter` と同方針).
//   - ChatPanel は `chat` をそのまま render するだけ (filter ロジックを持たない).
//   - system 行は uid を持たないので filter 対象外 (入退室/メンテ/kick 通知は
//     uid に依存せず重要、architecture.md §19.14 の方針は維持).
//
// Phase 6.7 R13 (consult 2026-05-05): 白背景 + 黒字化.
//   - 旧版 `OekakiChatJava/screenshot/room.png` のチャット欄は **白背景 + 黒テキスト**
//     のシンプルな見た目. 現状コードは dark soft bg + accent 色装飾でこの領域だけ
//     旧版から離れていたので白背景化する.
//   - log 枠: `bg-white text-black`, border 黒系細枠. font-mono は維持.
//   - system 行: 現状の `* {text}` 装飾は維持 (ユーザ指示「いじらなくていい」).
//     旧版の `********** SYSTEM MESSAGE: ... **********` 完全装飾は **不要**.
//     文字色は `text-gray-600` 程度の控えめ黒系.
//   - chat 行: 自分 = `text-blue-700`, 他者 = `text-black`. `: ` 区切りは控えめグレー.
//   - 入力欄: 白背景 + 黒文字 + 青系 border. 送信ボタンは `outsetFrame` 適用.
//
// Phase 6.7 サブ task 後追い (consult 2026-05-07):
//   - 発言小窓 (floating popup). 純粋ロジックは `floatingChatPopup.ts`、UI は
//     本ファイル末尾に inline で配置.
//     - 開く: document-level Ctrl+Enter / Cmd+Enter (旧版
//       `WhiteboardCanvas.java:2057` 踏襲) → `ChatPanelHandle.openFloatingPopup()`.
//       listener は `ClassicLayout.tsx` (Modern は `ModernLayout.tsx`) 側で配線.
//       修飾キー入りは「開くのみ」 (= 既に open なら何もしない).
//     - 閉じる: × ボタン / Esc.
//     - 2026-06-12 改訂: 旧「空 Enter 2 連打」での開閉 (開く側・閉じる側とも) は
//       撤去し、Ctrl/Cmd+Enter open + ×/Esc close に一本化. 「設定画面に列挙され
//       ＝発見可能なものを canonical とし、不可視の隠しジェスチャは設けない」原則
//       (docs `shortcut-settings.md`) に従う. Esc は標準モーダル慣例なので隠し
//       ジェスチャではない.
//   - 名前コピペ (MemberPanel ダブルクリック → chat input にユーザ名挿入).
//     ChatPanel は外部から呼べる `insertName(displayName)` を `forwardRef` +
//     `useImperativeHandle` で公開し、ClassicLayout が ref 経由で叩く.
//   - `/bellon roomin` (= 入室お知らせベル切替) のための ports 拡張.

import { MAX_CHAT_TEXT_LENGTH, MIN_CHAT_TEXT_LENGTH } from '@oekaki/protocol';
import {
  forwardRef,
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import {
  dispatchSlashCommand,
  parseSlashCommand,
  type SlashCommandPorts,
} from '../../../application/slashCommand.js';
import type { ChatEntry } from '../../../domain/RoomState.js';
import { useConnectionLocked } from '../../providers/ConnectionLockContext.js';
import {
  clampFloatPosition,
  type FloatPoint,
  floatPositionFromPointer,
} from '../shared/floatShellGeometry.js';
import { usePanelDragGrip } from '../shared/usePanelDragGrip.js';
import {
  closePopupState,
  type FloatingPopupState,
  openPopupState,
  popupStateAfterSubmit,
} from './floatingChatPopup.js';
import { outsetFrame } from './legacyFrames.js';
import { insertNameIntoDraft } from './memberDoubleClick.js';

/**
 * S3 スライス1 (floating-panels-2026-08-01.md §8): 旧 `clampPopupPosition` の既定
 * margin (8px) と揃える (= 挙動不変)。発言小窓の drag 移動は共有
 * `usePanelDragGrip` + `floatShellGeometry` を使う (= ColorWheelPalette / LayerPopup /
 * PenPopup と同じ移行)。
 */
const POPUP_EDGE_INSET_PX = 8;

/**
 * 2026-05-27 イマドキスクロール挙動 (consult 済): スクロール位置の metrics.
 * `scrollHeight - scrollTop - clientHeight` が「最下部からの距離 (px)」で、
 * これが閾値未満なら「ほぼ最下部にいる = 追従中」とみなす.
 */
export interface ChatScrollMetrics {
  readonly scrollTop: number;
  readonly scrollHeight: number;
  readonly clientHeight: number;
}

/**
 * 2026-05-27 イマドキスクロール挙動 (consult 済): 旧 Java 版踏襲の手動 toggle
 * 「あスクロール」(= `scrollLocked`) を廃止し、スクロール位置に基づく自動追従
 * 停止 + 「最新へ戻る」ボタン方式 (LINE/Discord/Slack 標準 UX) に置き換えた.
 *
 * 本関数は「今ほぼ最下部にいるか」を判定する純粋関数. 完全一致
 * (`scrollHeight - scrollTop === clientHeight`) は使わない — フォント / zoom /
 * サブピクセル丸めで端数が出て崩れるため、閾値 `threshold` を持たせ、最下部
 * からの距離が閾値未満なら「追従中」とみなす (= sticky to bottom).
 *
 * 切り出し理由は従来同様: useEffect / onScroll 内のロジックを node 環境で
 * testable にするため (vitest が React component を直接マウントしない MVP
 * 範囲では pure helper 経由で確認する).
 */
export const CHAT_NEAR_BOTTOM_THRESHOLD_PX = 50;

export const isChatNearBottom = (
  { scrollTop, scrollHeight, clientHeight }: ChatScrollMetrics,
  threshold: number = CHAT_NEAR_BOTTOM_THRESHOLD_PX,
): boolean => scrollHeight - scrollTop - clientHeight < threshold;

/**
 * server-stamped ts (= Broadcaster の `clock.now()` ms epoch、または
 * client 側 fallback の `Date.now()`) を JST `[HH:MM]` 文字列に整形する.
 *
 * `Intl.DateTimeFormat` に `timeZone: 'Asia/Tokyo'` を指定しているので、
 * ユーザの OS タイムゾーンに依存せず常に JST で表示される (= 海外から
 * 接続するユーザにも日本時間が見える、サービス運営側の時刻と一致).
 *
 * Formatter は module-level に 1 個キャッシュ (毎 render で new するのは
 * 無駄). 形式は 24h `HH:MM` 固定で、秒は表示しない (= チャットの可読性優先).
 */
const jstHhmmFormatter = new Intl.DateTimeFormat('ja-JP', {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
  timeZone: 'Asia/Tokyo',
});

export const formatJstHhmm = (ts: number): string => `[${jstHhmmFormatter.format(new Date(ts))}]`;

export interface SystemLineEntry {
  readonly ts: number;
  readonly text: string;
  /** Phase 9.6: メンテ告知 / お知らせ系の色分けに使う optional level. */
  readonly level?: 'info' | 'warning' | 'critical';
}

/**
 * `chat` と `system` の両配列を `ts` 昇順 (= 古い→新しい) で 1 本にマージした
 * render 入力. user 発言とシステム通知 (入退室・メンテ告知 etc) を時系列で
 * 混ぜて表示するために使う.
 *
 * 旧実装は system → chat の順で 2 ブロックを並べていたため、入室後に来た
 * システム通知が常に最上部に集約され「割り込みの時間関係が消える」バグが
 * あった (= 報告 2026-05-17). chat.ts と system.ts は同じ server-side ts
 * (`ev.ts`, Date.now() ms) を使っているのでそのまま比較できる.
 */
export type MergedChatLine =
  | { readonly kind: 'chat'; readonly entry: ChatEntry }
  | { readonly kind: 'system'; readonly entry: SystemLineEntry };

export const mergeChatAndSystem = (
  chat: readonly ChatEntry[],
  system: readonly SystemLineEntry[],
): readonly MergedChatLine[] => {
  const merged: MergedChatLine[] = [
    ...chat.map((entry): MergedChatLine => ({ kind: 'chat', entry })),
    ...system.map((entry): MergedChatLine => ({ kind: 'system', entry })),
  ];
  // ES2019+ Array.prototype.sort は stable. 同 ts は input order を保つので、
  // 上の concat 順 (chat 先 → system 後) により「同 ms に chat と system が
  // 並んだら chat を上」になる. ms 単位の衝突は事実上起きないため、表示
  // 順の任意性は気にしない (= server が ts 昇順で送ってくる前提).
  merged.sort((a, b) => a.entry.ts - b.entry.ts);
  return merged;
};

export interface ChatPanelProps {
  readonly chat: readonly ChatEntry[];
  readonly system: readonly SystemLineEntry[];
  readonly selfUid: string;
  readonly disabled?: boolean;
  readonly onSend: (text: string) => void;
  /**
   * Phase 7 Task 7-9: slash command 経由で `/rom`, `/clear`, `/imagepaste`
   * を捌くための side-effect ports. `useSession()` から ClassicLayout が
   * 配線して prop drill する (新たな専用 Context は導入しない — 確定方針).
   *
   * `imagepasteStart` は権限が無い (= `Perm.IMAGEPASTE` 未保有) ときは
   * `null` を渡す (= `/imagepaste` で warn line を出す).
   */
  readonly slashPorts: Omit<SlashCommandPorts, 'imagepasteStart'> & {
    /**
     * `null` のとき `/imagepaste` を実行しても warn line のみ.
     * non-null なら呼び出し時に file picker を開いた後、選択された File を
     * `useImagepaste.start` に渡す. ChatPanel 内に hidden な `<input>` を
     * 持っているため、ports 側は単に File を受け取るだけで良い.
     */
    readonly imagepasteOnFile: ((file: File) => void) | null;
  };
  /**
   * 2026-05-17 個人 UI スケール (consult 2026-05-17): 発言小窓 (Ctrl/Cmd+Enter
   * popup) は ChatPanel 内部の `position: fixed` popup で、親 wrapper の `zoom`
   * を引き継がない (CSS `zoom` は inherit ではないので、`position: fixed`
   * 要素は親の zoom 影響を受けない). 親 ChatPanel の見た目と歩調を合わせる
   * ため、popup 自身に `zoom` inline style として直接当てる経路.
   *
   * `undefined` のときは zoom 無指定 = 等倍. 後方互換のため optional.
   */
  readonly popupZoom?: number;
  /**
   * 2026-05-31 chat-scale-cleanup (alpha-feedback-2026-05-31.md §3 ID=E):
   * チャット欄内部 (log container / 通常入力欄 / 発言小窓 input) の文字サイズ
   * を em 倍率で拡縮する独立軸. 行高 / 列幅は変えず文字だけ拡大するため、
   * `style={{ fontSize: ${em}em }}` を該当 3 箇所に当てる. 既存 .text-xs を
   * ベースに `em` を解釈する想定.
   *
   * `undefined` のときは inline 未指定 = 等倍 (= 既存 .text-xs のまま). 後方
   * 互換のため optional.
   */
  readonly fontScaleEm?: number;
}

/**
 * Phase 6.7 サブ task 後追い (consult 2026-05-07): ChatPanel の外部から呼べる
 * 命令的 API. MemberPanel のダブルクリックで `displayName` を入力欄に挿入する
 * ためにだけ存在する. ClassicLayout が `useRef<ChatPanelHandle>` を作って
 * `<ChatPanel ref={...} />` で渡し、`onMemberDoubleClick` で `.insertName()`
 * を呼ぶ. props drill 経路と比べて draft state を ChatPanel 内に閉じ込めた
 * まま外から触れる、最小侵襲な設計.
 */
export interface ChatPanelHandle {
  /**
   * `displayName` を chat input の現在 caret 位置に挿入する.
   * caret が取れない / 入力欄未 mount のときは末尾に追記する fallback.
   * 挿入後は input にフォーカスする (= ユーザがそのまま続けて入力できるよう).
   */
  insertName: (displayName: string) => void;
  /**
   * 発言小窓を開く (= ClassicLayout の document-level Ctrl+Enter / Cmd+Enter
   * listener から呼ばれる経路, consult 2026-05-07). 旧版
   * `WhiteboardCanvas.java:2057` の Ctrl+Enter は dialog を「開く」のみで
   * 「閉じる」ことはなかったため (= 閉じるのは × ボタン / Esc),
   * すでに open のときは何もしない (idempotent).
   * 呼び出し後は popup 内 input にフォーカスを当てたいが, mount 直後は ref が
   * 揃わないので focus 制御は ChatPanel 内部で別途行う (= 既存設計の影響を
   * 抑えるため最低限のセマンティクスのみここで定義).
   */
  openFloatingPopup: () => void;
}

export const ChatPanel = forwardRef<ChatPanelHandle, ChatPanelProps>(function ChatPanel(
  { chat, system, selfUid, disabled, onSend, slashPorts, popupZoom, fontScaleEm },
  ref,
) {
  // 2026-05-31 chat-scale-cleanup: fontScaleEm が undefined のときは inline
  // 未指定 (= 既存 .text-xs のまま). 数値が来たときだけ inline style に流す.
  const fontSizeStyle: { fontSize?: string } =
    fontScaleEm === undefined ? {} : { fontSize: `${fontScaleEm}em` };
  // 再接続中は送れない (docs/_wip/reconnect-draw-lock-2026-10-12.md): 入力欄は無効にせず
  // (打ちかけの文字とフォーカスを残す)、送信だけ止める。
  const connectionLocked = useConnectionLocked();
  const lockedNoteId = useId();
  const [draft, setDraft] = useState('');
  const logRef = useRef<HTMLDivElement | null>(null);
  // 2026-05-27 イマドキスクロール挙動 (consult 済): 「最下部に追従中か」の state.
  // デフォルト true (= sticky to bottom). ユーザが上にスクロールして閾値を超えると
  // false になり、新着 entry が来てもスクロール位置を動かさない (= 読書位置保持).
  // 最下部へ戻る (= 「最新へ」ボタン / 手動スクロールで最下部到達) と true に復帰.
  const [stickToBottom, setStickToBottom] = useState(true);
  // 追従停止中に積もった未読件数. 0 のときバッジ非表示. stick 復帰でリセット.
  const [unreadCount, setUnreadCount] = useState(0);
  // 自分の発言送信直後に立てるフラグ. 次の chat 更新 (= 自分の entry が props に
  // 反映される render) で、追従停止中でも強制的に最下部へスクロールするために使う
  // (= 送信は「見たい」意思表示なので追従を再開する). effect が読んで消費する ref.
  const forceScrollOnNextUpdateRef = useRef(false);
  // Phase 6.7 サブ task 後追い: 入力欄 (= 通常本体側) の <input> ref. ChatPanel
  // 外部 (= MemberPanel ダブルクリック経路) から caret 位置に名前を挿入する
  // ため、`useImperativeHandle` で公開する `insertName()` がここを参照する.
  const inputRef = useRef<HTMLInputElement | null>(null);
  // Phase 7 Task 7-9: hidden file input for `/imagepaste`. Programmatic
  // `.click()` opens the OS picker; `onChange` then forwards the selected
  // File to `slashPorts.imagepasteOnFile`. `accept` mirrors the schema-
  // accepted MIME types (PNG / JPEG / GIF) so the picker filters at the OS
  // level — defensive `useImagepaste.start` re-validates anyway.
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Phase 6.7 サブ task 後追い (consult 2026-05-07): 発言小窓.
  // 状態:
  //   - popupState: 'closed' | 'open'. 開く = Ctrl/Cmd+Enter、閉じる = ×/Esc
  //     (2026-06-12 で空 Enter 2 連打開閉は撤去).
  //   - popupPos: 画面上の位置 (px). 初期値は右下寄り (右下推奨は consult 確定).
  //   - S3 スライス1: ドラッグ機構は共有 `usePanelDragGrip` (grabOffsetRef で
  //     実測 grab-offset を保持する).
  const [popupState, setPopupState] = useState<FloatingPopupState>('closed');
  const [popupPos, setPopupPos] = useState<FloatPoint>({ x: 24, y: 24 });
  const [popupDraft, setPopupDraft] = useState('');
  const popupInputRef = useRef<HTMLInputElement | null>(null);
  // 発言小窓 root 要素 ref. ドラッグ / resize clamp で実寸 (getBoundingClientRect)
  // を読む. popup は `zoom` を当てているが getBoundingClientRect は zoom 適用後の
  // client px を返すので、同じ client px の left/top・window.innerWidth とそのまま
  // 比較でき、zoom 補正は不要 (popup の style コメント参照).
  const popupRef = useRef<HTMLDivElement | null>(null);

  // Phase 6.7 サブ task 後追い: 外部から `insertName(displayName)` を呼べる
  // ようにする. ClassicLayout が ref で受け取り、MemberPanel ダブルクリック
  // ハンドラから叩く. caret 位置を `<input>.selectionStart/End` で読み、純粋
  // ヘルパ `insertNameIntoDraft` に委譲して計算 → setDraft + caret 復元.
  useImperativeHandle(ref, () => ({
    insertName: (displayName) => {
      const el = inputRef.current;
      const caret =
        el !== null && el.selectionStart !== null && el.selectionEnd !== null
          ? { start: el.selectionStart, end: el.selectionEnd }
          : null;
      setDraft((prev) => {
        const result = insertNameIntoDraft(prev, displayName, caret);
        // caret 復元は次フレームで <input> が新値を反映してから. requestAnimationFrame
        // を 1 段挟むのが React 流儀の一般的方法だが、`<input value=...>` は
        // controlled なので setSelectionRange は同期で動く (= React が draft 更新
        // と同フレームで <input>.value を書き戻す保証はないが、focus + setSelection
        // は次の microtask で反映される). queueMicrotask で 1 拍待たせる.
        queueMicrotask(() => {
          const cur = inputRef.current;
          if (cur === null) return;
          cur.focus();
          try {
            cur.setSelectionRange(result.nextCaret, result.nextCaret);
          } catch {
            // type=text 以外で setSelectionRange が throw するケースの defensive.
          }
        });
        return result.nextDraft;
      });
    },
    // Phase 6.7 サブ task 後追い (consult 2026-05-07, 追加修正): ClassicLayout
    // の document-level Ctrl+Enter / Cmd+Enter listener から呼ばれる. 旧版
    // 踏襲で「開くのみ」 — open 中は何もしない (= 二重呼び出しに対し idempotent,
    // toggle ではない). 開いた直後に小窓 input にフォーカスしたいが、popup は
    // この呼び出し後の re-render で mount されるので、queueMicrotask で 1 拍
    // 待ってから focus を試みる (= ref が揃ったタイミング).
    openFloatingPopup: () => {
      setPopupState(openPopupState);
      queueMicrotask(() => {
        popupInputRef.current?.focus();
      });
    },
  }));

  // 新着 entry の検出用カウンタ. counter を effect deps に渡し、slice の各
  // プロパティを冗長に列挙せずに biome / React-hooks 依存解析を満たす.
  // 2026-05-06 仕様統一: chat は SessionProvider で future-only filter 済
  // (= reducer に入った時点で filter 対象は捨てられている), ここでは chat を
  // そのまま参照すれば良い.
  const totalEntries = chat.length + system.length;

  // 最下部へスクロールする helper. zoom (`popupZoom` / 親 wrapper の CSS zoom)
  // 内側にあっても scrollTop / scrollHeight は zoom 適用後の同一座標系の値なので、
  // `scrollTop = scrollHeight` で最下部に寄せる計算は zoom で破綻しない.
  const scrollToBottom = useCallback((): void => {
    const el = logRef.current;
    if (el !== null) el.scrollTop = el.scrollHeight;
  }, []);

  // 2026-05-27 イマドキスクロール挙動 (consult 済): 新着 entry 受信時のスクロール.
  //   - 自分の発言送信直後 (forceScrollOnNextUpdateRef) は追従停止中でも最下部へ
  //     スクロールし、追従を再開する (送信 = 見たい意思表示).
  //   - 追従中 (stickToBottom) なら常に最下部へ追従.
  //   - 追従停止中の他人発言ではスクロール位置を動かさず、未読件数を加算する
  //     (= 「最新へ」ボタンのバッジに反映、読書位置を保持).
  // リサイズ / フォントサイズ変更で stickiness が壊れないよう、追従中は entry 増加
  // 以外の再 render では走らないが、追従中であれば次の entry で再度最下部に寄る.
  const prevTotalRef = useRef(totalEntries);
  useEffect(() => {
    const prevTotal = prevTotalRef.current;
    prevTotalRef.current = totalEntries;
    const added = totalEntries - prevTotal;

    if (forceScrollOnNextUpdateRef.current) {
      forceScrollOnNextUpdateRef.current = false;
      setStickToBottom(true);
      setUnreadCount(0);
      scrollToBottom();
      return;
    }
    if (stickToBottom) {
      scrollToBottom();
      return;
    }
    // 追従停止中: 増えた分を未読として加算 (entry が増えたときのみ).
    if (added > 0) setUnreadCount((c) => c + added);
  }, [totalEntries, stickToBottom, scrollToBottom]);

  // スクロールイベントで「最下部にいるか」を判定し追従状態を更新する.
  // 閾値 (`isChatNearBottom`) 未満なら追従再開 (= 未読リセット)、超えたら追従停止.
  const onLogScroll = useCallback((): void => {
    const el = logRef.current;
    if (el === null) return;
    const nearBottom = isChatNearBottom({
      scrollTop: el.scrollTop,
      scrollHeight: el.scrollHeight,
      clientHeight: el.clientHeight,
    });
    setStickToBottom(nearBottom);
    if (nearBottom) setUnreadCount(0);
  }, []);

  // 「最新へ」ボタン: 最下部までスクロールし追従を再開する.
  const jumpToLatest = useCallback((): void => {
    scrollToBottom();
    setStickToBottom(true);
    setUnreadCount(0);
  }, [scrollToBottom]);

  /**
   * 共通 submit ロジック. 本体 (`<input>`) と発言小窓 (popup) のどちらからも
   * 呼ぶ. caller が trim 済 (or non-trim) の text を渡し、ports は同一を使う.
   * 戻り値は「処理されたか」(= input をクリアして良いか). 単に false を
   * 返すと「未送信」(= 入力欄に残しておく) のサイン.
   */
  const submitText = (text: string): boolean => {
    const trimmed = text.trim();
    if (trimmed.length === 0) return false;
    // Phase 7 Task 7-9: slash command を先に処理. parser が 'not-slash' を
    // 返したときだけ通常の chat 送信に倒す. ROM 中でも slash command (`/rom`
    // で ROM 解除など) は撃てるべきなので `disabled` ガードよりも上で処理する.
    const parsed = parseSlashCommand(trimmed);
    if (parsed.kind !== 'not-slash') {
      // サーバに送るスラッシュコマンド (/rom, /imagepaste) は再接続中は止める。
      // /help /clear など自分の画面だけで済むものは通す。
      if (
        connectionLocked &&
        (parsed.kind === 'ok' || parsed.kind === 'extra-args') &&
        (parsed.name === 'rom' || parsed.name === 'imagepaste')
      ) {
        return false;
      }
      // dispatchSlashCommand は `imagepasteStart` を呼ぶことで file picker
      // を開く. ChatPanel が `<input>` を所有しているので、ports 側に
      // `imagepasteStart: () => fileInputRef.current?.click()` を合成する.
      const imagepasteStart =
        slashPorts.imagepasteOnFile === null
          ? null
          : (): void => {
              fileInputRef.current?.click();
            };
      dispatchSlashCommand(parsed, {
        pushSystemLine: slashPorts.pushSystemLine,
        setRom: slashPorts.setRom,
        currentIsRom: slashPorts.currentIsRom,
        clearChatLog: slashPorts.clearChatLog,
        imagepasteStart,
        currentBellOnRoomin: slashPorts.currentBellOnRoomin,
        setBellOnRoomin: slashPorts.setBellOnRoomin,
        openToolFloat: slashPorts.openToolFloat,
        closeToolFloat: slashPorts.closeToolFloat,
        openChatFloat: slashPorts.openChatFloat,
        closeChatFloat: slashPorts.closeChatFloat,
      });
      return true;
    }
    // 通常 chat: ROM 中は送れない. server schema が要求する length も満たすこと.
    if (disabled === true) return false;
    if (connectionLocked) return false;
    if (trimmed.length < MIN_CHAT_TEXT_LENGTH || trimmed.length > MAX_CHAT_TEXT_LENGTH) {
      return false;
    }
    // 2026-05-27 イマドキスクロール挙動 (consult 済): 自分の発言は常に最下部へ.
    // 送信した chat は server 経由で自分の chat.message として戻り、props の
    // `chat` に積まれた次の render で auto-scroll effect が消費する. 追従停止中
    // でもこのフラグで最下部へ戻し追従を再開する (= 送信 = 見たい意思表示).
    forceScrollOnNextUpdateRef.current = true;
    onSend(trimmed);
    return true;
  };

  const submit = (): void => {
    if (submitText(draft)) setDraft('');
  };
  const submitPopup = (): void => {
    // 旧版踏襲 (`OekakiChatJava/.../ChatPanel.java:614-617`): 非空送信時は
    // 発言小窓を自動で閉じる. 空送信は元々 `submitText` が false を返すので
    // ここに到達せず、閉じる経路は × ボタン / Esc のみが担当.
    // `submitText` が true を返すのは「slash command 実行」または「通常 chat
    // 送信成功」のいずれかで、どちらも『何かを送信した = popup の用は済んだ』
    // 状態なので閉じて良い (ROM 中 + 非 slash の chat 送信失敗時は false が
    // 返るので popup は開いたまま — UX 上正しい). 状態遷移は純粋ヘルパ
    // `popupStateAfterSubmit` に委譲して node env で test 可能にする.
    const submitted = submitText(popupDraft);
    if (submitted) setPopupDraft('');
    setPopupState((cur) => popupStateAfterSubmit(cur, submitted));
  };

  // 発言小窓の実寸を読む helper. ref 未取得時 (= open 直後の初回 render 前等) は
  // w-[420px] 定数 + 概算高さ (= title bar + 1 行 input form) を fallback.
  // ref のみ参照するので useCallback([]) で安定参照にして effect deps に渡せる.
  const measurePopup = useCallback((): { width: number; height: number } => {
    const el = popupRef.current;
    if (el !== null) {
      const r = el.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) return { width: r.width, height: r.height };
    }
    return { width: 420, height: 80 };
  }, []);

  // ─── window resize で画面外に取り残されたら引き戻す (popup open 時のみ). ────
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (popupState !== 'open') return;
    const onResize = (): void => {
      setPopupPos((cur) =>
        clampFloatPosition(
          cur,
          measurePopup(),
          { width: window.innerWidth, height: window.innerHeight },
          POPUP_EDGE_INSET_PX,
        ),
      );
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [popupState, measurePopup]);

  // 実測 grab-offset (= 掴んだ点と popup 左上の相対距離、S2 §11 と同じ流儀).
  const grabOffsetRef = useRef<FloatPoint | null>(null);
  const popupGrip = usePanelDragGrip({
    onDragStart: (offset) => {
      grabOffsetRef.current = offset;
    },
    onDragMove: (clientX, clientY) => {
      const offset = grabOffsetRef.current ?? { x: 0, y: 0 };
      setPopupPos(
        clampFloatPosition(
          floatPositionFromPointer(clientX, clientY, offset),
          measurePopup(),
          { width: window.innerWidth, height: window.innerHeight },
          POPUP_EDGE_INSET_PX,
        ),
      );
    },
    onDragEnd: () => {
      grabOffsetRef.current = null;
    },
    measureGrabOffset: (e) => {
      const rect = popupRef.current?.getBoundingClientRect();
      if (!rect) return { x: 0, y: 0 };
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    },
  });

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    // 同じファイルを 2 回連続で選んでも change が発火するよう reset する.
    e.target.value = '';
    if (file === undefined || slashPorts.imagepasteOnFile === null) return;
    slashPorts.imagepasteOnFile(file);
  };

  return (
    // Phase 6.7 R13: 白背景を section レベルで持たせる. log + form の間の
    // mt-1 gap が親 (outsetFrame) の背景色と混ざらないよう統一.
    <section aria-label="チャット" className="flex h-full flex-col bg-white p-1">
      {/* Phase 6.7 R13: log 枠は旧版踏襲で白背景 + 黒字 + 黒系細枠. font-mono / text-xs は維持.
          consult 2026-05-09: iPad Apple Pencil で描画中、メッセージ履歴のテキストが
          ダブルタップ等のジェスチャで選択され描画が中断する問題への対策.
          - `select-none` で常時テキスト選択を抑止 (= Pencil の単語選択ジェスチャ発火しない).
          - `tabIndex={0}` + `focus:select-text focus-within:select-text` で、
            ユーザが意図的にこのコンテナをタップ / クリック / Tab で focus したときだけ
            選択可能化 (= コピペ用途を維持).
          - `focus:outline-none focus-within:outline-none` で focus ring を抑制
            (= 視覚的にうるさいため、キーボード focus 時の表示は別途検討).
          - `role="log"` / `aria-label` / `aria-live="polite"` で a11y 維持
            (= スクリーンリーダーで「チャット履歴」と認識され、新着が読み上げられる). */}
      {/* 2026-05-27 イマドキスクロール挙動 (consult 済): log 枠を relative wrapper で
          包み、「最新へ」フローティングボタンを log 欄の最下部付近に重ねて出す.
          wrapper は `flex-1 min-h-0` で残り高さを占有し、内側の log を `h-full` に. */}
      <div className="relative flex min-h-0 flex-1 flex-col">
        <div
          ref={logRef}
          onScroll={onLogScroll}
          // biome-ignore lint/a11y/noNoninteractiveTabindex: consult 2026-05-09 で意図的に non-interactive な log container を focus 可能にしている (= デフォルト select-none で Pencil ジェスチャ抑止しつつ、focus 時のみ select-text 切替でコピペ可能化). role="log" + aria-live で SR セマンティクスは維持.
          tabIndex={0}
          role="log"
          aria-label="チャット履歴"
          aria-live="polite"
          className="h-full select-none overflow-y-auto border border-black bg-white p-2 font-mono text-xs text-black focus:select-text focus:outline-none focus-within:select-text focus-within:outline-none"
          // 2026-05-31 chat-scale-cleanup (alpha-feedback §3 ID=E): チャット文字
          // サイズ独立軸. log 全体の text-xs を em 倍率で拡大 (= 各 <p> / <span>
          // が em を継承する). 等倍時は inline 未指定 = 既存 text-xs のまま.
          style={fontSizeStyle}
        >
          {mergeChatAndSystem(chat, system).map((line) => {
            if (line.kind === 'system') {
              const s = line.entry;
              // `ts` is wall-clock + the text discriminates duplicate timestamps.
              // Phase 6.7 R13: `* {text}` 装飾は維持.
              // Phase 9.6: level で色分け (= メンテ告知 + announce 用). 既存挙動の
              // 控えめグレーは level 未指定 / info を default として保つ.
              const colorCls =
                s.level === 'critical'
                  ? 'font-bold text-red-700'
                  : s.level === 'warning'
                    ? 'font-bold text-orange-700'
                    : 'text-gray-600';
              return (
                <p key={`sys-${s.ts}-${s.text}`} className={colorCls}>
                  <span className="text-gray-400">{formatJstHhmm(s.ts)} </span>* {s.text}
                </p>
              );
            }
            const c = line.entry;
            const isSelf = c.uid === selfUid;
            return (
              <p key={`chat-${c.seq}`} className="break-words">
                {/* Phase 6.7 R13: 自分 = 青, 他者 = 黒. `: ` は控えめグレー, 本文は黒. */}
                <span className="text-gray-400">{formatJstHhmm(c.ts)} </span>
                {/* 2026-05-17 displayName 改行抑止 + 省略: chat 行幅が狭い時に sender 名が
                  折り返さず `…` で省略するように. 本文 (`c.text`) 側は break-words の
                  まま折り返したいので、displayName span のみに `inline-block + max-w +
                  truncate (= overflow-hidden + text-ellipsis + whitespace-nowrap) +
                  align-bottom (inline-block の baseline 揃え)` を当てる. chat 行は
                  実用上十分幅があるので max-w は 10em と緩めに. */}
                <span
                  className={`inline-block max-w-[10em] truncate whitespace-nowrap align-bottom ${
                    isSelf ? 'font-bold text-blue-700' : 'text-black'
                  }`}
                >
                  {c.displayName}
                </span>
                <span className="text-gray-500">: </span>
                <span className="text-black">{c.text}</span>
              </p>
            );
          })}
        </div>

        {/* 2026-05-27 イマドキスクロール挙動 (consult 済): 「最新へ」フローティング
            ボタン. 追従停止中 (= !stickToBottom) のみ表示し、log 欄の最下部中央に
            重ねる. クリックで最下部へスクロール → 追従再開. 未読件数があれば
            バッジで件数を出す (追従停止中に積もった他人発言の数). zoom 内側に
            あっても absolute は wrapper 基準なので位置計算は zoom で破綻しない. */}
        {!stickToBottom && (
          <button
            type="button"
            onClick={jumpToLatest}
            aria-label={unreadCount > 0 ? `最新へ (未読 ${unreadCount} 件)` : '最新へ'}
            className="-translate-x-1/2 absolute bottom-2 left-1/2 flex items-center gap-1 rounded-full border border-black bg-white px-3 py-1 text-[10px] font-bold text-black shadow-md hover:bg-gray-100"
          >
            {unreadCount > 0 && (
              <span className="inline-flex min-w-[1.25em] items-center justify-center rounded-full bg-blue-600 px-1 text-[9px] text-white">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
            <span>最新へ ↓</span>
          </button>
        )}
      </div>

      <form
        className="mt-1 flex gap-1"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        {/* Phase 6.7 R13: 入力欄は白背景 + 黒文字 + 青系 border. */}
        <input
          ref={inputRef}
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          data-manual="together.chat"
          // 2026-06-12: 旧「空 Enter 2 連打で発言小窓を開く」検出は撤去した
          // (Ctrl/Cmd+Enter 一本化、原則「設定画面に出るものが正」). 通常の
          // チャット送信は form の onSubmit が担うため、ここに Enter ハンドラは
          // 不要 (空 Enter は submit → submitText('') が false で no-op).
          maxLength={MAX_CHAT_TEXT_LENGTH}
          aria-describedby={connectionLocked ? lockedNoteId : undefined}
          // Phase 7 Task 7-9: ROM 中でも slash command は撃てるべきなので
          // `<input>` 自体は無効化しない. 送信ボタン側で chat 送信のみガード.
          placeholder={
            disabled === true
              ? 'ROM 中: /rom で解除、/help でヘルプ'
              : 'メッセージを入力 (Enter で送信、/help でヘルプ)'
          }
          // 2026-09-22 追補: `flex-1` だけだと flex item の既定 `min-width: auto`
          // (= placeholder 文字列由来の min-content 幅) がコンテナ幅を上回り、
          // Modern の右ドック (260px 固定, workspaceLayout.ts) では入力欄が
          // ドック右端をわずかにはみ出す (manual:capture で発覚、hotspot
          // `together.chat` が画像外に落ちた)。`min-w-0` で flex item の縮小下限を
          // 外し、ドック幅に収める (見た目・機能は不変、はみ出しのみ解消)。
          className="min-w-0 flex-1 rounded border border-[var(--color-border)] bg-white px-2 py-1 text-xs text-black outline-none focus:border-[var(--color-accent)] disabled:opacity-50"
          // 2026-05-31 chat-scale-cleanup: 入力欄文字も log と一緒に拡縮.
          style={fontSizeStyle}
        />
        {/* Phase 6.7 R13: 送信ボタンは旧版風 outset frame + 白背景 + 黒文字. アクセシビリティ確保のためボタンは残す. */}
        <button
          type="submit"
          // Phase 7 Task 7-9: ROM 中でも slash command (`/rom` 解除等) は通すので
          // 送信ボタンも空でない限り disable しない. 通常 chat の length / ROM
          // チェックは `submit()` 内側で行う.
          disabled={draft.trim().length === 0 || connectionLocked}
          className={`bg-white px-3 py-1 text-xs font-bold text-black disabled:opacity-50 ${outsetFrame}`}
        >
          送信
        </button>
        {/* Hidden file input — programmatically opened by `/imagepaste`.
            `display: none` ではなく `hidden` 属性で十分 (Safari iOS 16 でも
            programmatic click が動く), ToolPanel.tsx の同設計に揃えた. */}
        {slashPorts.imagepasteOnFile !== null && (
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/gif"
            hidden
            aria-hidden="true"
            tabIndex={-1}
            onChange={onFileChange}
          />
        )}
      </form>
      {connectionLocked && (
        <p id={lockedNoteId} data-oekaki-chat-locked-note className="mt-0.5 text-right text-[10px]">
          つながったら送れます
        </p>
      )}

      {/* Phase 6.7 サブ task 後追い (consult 2026-05-07): 発言小窓.
          Ctrl/Cmd+Enter で出現する floating panel. ChatPanel 本体は隠さず追加表示.
          - position: 画面 viewport 基準の `position: fixed` (= 親の overflow に
            縛られず、どこにいても画面内のどこに置いても良い).
          - drag: タイトルバー (= 「発言小窓」見出し部) を pointerdown → move の
            標準パターンで掴む. setPointerCapture で外に出ても追従させる.
          - 小窓内 input は通常 chat と同じ `submitText` を使うので slash
            command も撃てる. 閉じる手段は × ボタン / Esc (2026-06-12 で空 Enter
            2 連打 close は撤去).
          - ROM / disabled の文字も同じく placeholder で出す. */}
      {popupState === 'open' && (
        // 横幅 420px (consult 2026-05-07 追加修正): 元の `w-64` (= 256px) は
        // 旧版 dialog (= 大きめの独立 window) と比べて窮屈だったので拡大.
        // 1 行 input + 送信ボタンの実用幅 + ChatPanel 本体の入力欄横幅と
        // 整合する範囲. height はタイトルバー + 1 行 input のままで変更なし.
        <div
          ref={popupRef}
          role="dialog"
          aria-label="発言小窓"
          // 2026-06-12: 標準モーダル慣例で Esc で閉じる (= 空 Enter 2 連打 close
          // 撤去の代替キーボード経路). popup root に capture することで、入力欄に
          // focus があっても popup 内のどこにいても Esc → close が効く. Esc は
          // 「隠しジェスチャ」ではなくモーダル標準なので原則 (設定画面に出るものが
          // 正) と矛盾しない.
          onKeyDown={(e) => {
            // IME 変換中の Esc は変換キャンセル用なので popup close に流さない.
            if (e.key === 'Escape' && !e.nativeEvent.isComposing) {
              e.preventDefault();
              setPopupState(closePopupState);
            }
          }}
          className={`fixed z-50 w-[420px] bg-white text-black ${outsetFrame}`}
          // 2026-05-17 個人 UI スケール (consult 2026-05-17): popup 自身に zoom
          // を当てる. CSS `zoom` は親要素から inherit されないため、`position:
          // fixed` popup は親 wrapper の zoom 影響を受けない. ChatScale を反映
          // させるため、popup 自身で zoom を持つ. left/top は popup の親 (=
          // viewport) 座標系で解釈されるので popup 自身の zoom には影響されず
          // raw px のまま使える (= drag handler の座標計算が壊れない).
          // `popupZoom` undefined のときは zoom 指定なしで等倍.
          style={{ left: popupPos.x, top: popupPos.y, zoom: popupZoom }}
        >
          {/* タイトルバー = drag handle.
              ペンタブ対策: `touchAction: 'none'` で OS の pan / scroll ジェスチャ
              解釈を抑止する (= 解釈されると pointercancel が飛んで drag が途切れる、
              LayerPopup / PreviewOverlay / ColorWheelPalette と同じ修正). */}
          <div
            className="flex cursor-move select-none items-center justify-between border-b border-black bg-[var(--color-bg-soft)] px-2 py-1 text-[10px] font-bold text-white"
            style={{ touchAction: 'none' }}
            onPointerDown={popupGrip.onPointerDown}
            onPointerMove={popupGrip.onPointerMove}
            onPointerUp={popupGrip.onPointerUp}
            onPointerCancel={popupGrip.onPointerCancel}
            onLostPointerCapture={popupGrip.onLostPointerCapture}
          >
            <span>発言小窓</span>
            <button
              type="button"
              aria-label="発言小窓を閉じる"
              // pointerdown が親 (= title bar) に bubble すると `setPointerCapture`
              // が走って × ボタンへの click event が dispatch されなくなる
              // (= capture target で pointerup を受けるため click が成立しない).
              // 親への propagation を止めて drag を開始させない (consult 2026-05-07
              // バグ修正).
              onPointerDown={(e) => e.stopPropagation()}
              // 「閉じる」は常に直接 `'closed'` を set する (= toggle や idempotent
              // open setter を流用しない). 二重 click 等のレースでも確実に閉じる.
              onClick={() => setPopupState(closePopupState)}
              className="border border-black bg-white px-2 py-0 text-[10px] font-bold text-black"
            >
              ×
            </button>
          </div>
          <form
            className="flex gap-1 p-2"
            onSubmit={(e) => {
              e.preventDefault();
              submitPopup();
            }}
          >
            <input
              ref={popupInputRef}
              type="text"
              value={popupDraft}
              onChange={(e) => setPopupDraft(e.target.value)}
              maxLength={MAX_CHAT_TEXT_LENGTH}
              aria-describedby={connectionLocked ? `${lockedNoteId}-popup` : undefined}
              placeholder={
                disabled === true
                  ? 'ROM 中: /rom で解除'
                  : 'メッセージ (Enter で送信、Esc / × で閉じる)'
              }
              // 2026-06-12: 空 Enter 2 連打 close は撤去. 閉じるのは × / Esc
              // (Esc は popup root の onKeyDown が拾う). 通常の送信は form の
              // onSubmit が担うのでここに Enter ハンドラは不要.
              className="flex-1 rounded border border-[var(--color-border)] bg-white px-2 py-1 text-xs text-black outline-none focus:border-[var(--color-accent)]"
              // 2026-05-31 chat-scale-cleanup: 発言小窓 input の文字も
              // 通常入力と同じ em 倍率で拡縮 (= 3 箇所統一).
              style={fontSizeStyle}
            />
            <button
              type="submit"
              disabled={popupDraft.trim().length === 0 || connectionLocked}
              className={`bg-white px-3 py-1 text-xs font-bold text-black disabled:opacity-50 ${outsetFrame}`}
            >
              送信
            </button>
          </form>
          {connectionLocked && (
            <p id={`${lockedNoteId}-popup`} className="px-2 pb-1 text-right text-[10px]">
              つながったら送れます
            </p>
          )}
        </div>
      )}
    </section>
  );
});
